import clsx from 'clsx';
import React from 'react';

import {
    AtomButton,
    ButtonIcon,
    type IconDefinition,
    Tooltip,
    Truncatable,
    type TruncatableProps,
} from '@gitbook/spine-react';

/**
 * Control in the agent dock — a tab, the Agent button, or the history button.
 *
 * Purpose-built rather than Spine's `Button` for two reasons. It fills with an opaque colour —
 * `bg-neutral-ui-active` — and its hover and active states are deliberately *identical*: a tab
 * carries a close control layered on top, and hovering that takes the pointer off the button, so
 * any difference between the two states shows up as a flicker while reaching for the X.
 */
export function AgentDockButton({
    ref,
    ...props
}: {
    /** Rendered before the label. */
    icon?: IconDefinition | React.ReactNode;
    /**
     * Omitted for an icon-only control, where `label` becomes both the accessible name and the
     * tooltip — the same deal Spine's own `Button` offers an `iconOnly` one.
     */
    children?: React.ReactNode;
    label: string;
    /** Held in the filled state, which is the same fill hovering produces. */
    active?: boolean;
    /** Keeps the trailing padding clear of the close control a tab layers over its edge. */
    closable?: boolean;
    /**
     * Fill whenever the *group* is hovered rather than this button. Lets a tab stay filled
     * while the pointer is on the close control layered over it.
     */
    fillOnGroupHover?: boolean;
    /**
     * Let the control give up width when its row runs out, truncating its label instead of
     * overflowing. A prop rather than a class from the call site: both settings are `flex-shrink`
     * utilities, and which one won would come down to the order Tailwind emitted them in.
     */
    shrinkable?: boolean;
    className?: string;
    labelClassName?: string;
    /**
     * Tooltip shown when the label is too narrow for its text — and only then, since a label
     * the user can already read in full needs no tooltip at all.
     */
    labelTooltipProps?: TruncatableProps['tooltipProps'];
    onPress?: () => void;
} & Omit<React.ComponentPropsWithRef<typeof AtomButton>, 'children' | 'onPress'>) {
    const {
        icon,
        children,
        closable,
        label,
        active,
        fillOnGroupHover,
        shrinkable,
        className,
        labelClassName,
        labelTooltipProps,
        ...rest
    } = props;

    // Wrapped here rather than around the button at the call site: a menu or popover trigger clones
    // the element it is given, so a tooltip put between the two would take the props that open the
    // overlay. Spine's `Button` nests them the same way, for the same reason.
    return (
        <Tooltip label={children ? undefined : label}>
            <AtomButton
                ref={ref}
                aria-label={children ? undefined : label}
                {...rest}
                className={clsx(
                    'flex h-6 items-center gap-1.5 rounded-md px-2 text-ui-small transition-colors',
                    shrinkable ? 'min-w-0 shrink' : 'shrink-0',
                    closable ? 'pr-3' : null,
                    // Same fill for hover and active, so moving between the two never changes it.
                    // Spelled out at each use rather than shared through a constant: Tailwind only
                    // generates classes it can read literally in the source. It is the opaque
                    // neutral on purpose — Spine's `blank` fills with the alpha scale, and a
                    // gradient fading to a translucent colour tints what is under it instead of
                    // covering it, which is why the close control could never hide the title.
                    active ? 'bg-neutral-ui-active text-base' : 'text-muted',
                    !active &&
                        (fillOnGroupHover
                            ? 'group-hover:bg-neutral-ui-active group-hover:text-base group-has-[:focus-visible]:bg-neutral-ui-active group-has-[:focus-visible]:text-base'
                            : 'hover:bg-neutral-ui-active hover:text-base'),
                    // A plain control marks keyboard focus with the ring. A tab takes the hover
                    // fill instead (the group-has fill above): a ring fights the close control's
                    // gradient overlay, and keyboard focus on the close control has to fill the
                    // whole tab anyway for that gradient to resolve into anything.
                    fillOnGroupHover ? null : 'data-focus-visible:focus-ring',
                    className
                )}
            >
                {icon ? <AgentDockButtonIcon icon={icon} /> : null}
                {children ? (
                    // Truncates itself, and carries the tooltip only while it is actually cut off.
                    <Truncatable className={labelClassName} tooltipProps={labelTooltipProps}>
                        {children}
                    </Truncatable>
                ) : null}
            </AtomButton>
        </Tooltip>
    );
}

function AgentDockButtonIcon(props: { icon: IconDefinition | React.ReactNode }) {
    const { icon } = props;

    if (React.isValidElement(icon)) {
        return icon;
    }

    return <ButtonIcon icon={icon as IconDefinition} size="small" />;
}
